import moment from 'moment'
import { NotificationBarConfig } from "../context/notificationContext";
const bucketURL = process.env.REACT_APP_AWS_CLOUD_FRONT_URL

export const formatDateTimeInTimeZone = (
    value: string | Date | null | undefined,
    timeZone = 'Asia/Kolkata',
): string => {
    if (!value) return '-'

    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return '-'

    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h12',
    }).formatToParts(date)

    const part = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find(item => item.type === type)?.value || ''

    return `${part('day')} ${part('month')} ${part('year')}, ${part('hour')}:${part('minute')}:${part('second')} ${part('dayPeriod')}`
}

export function parseSearchParams(searchParams: URLSearchParams, key: string): any {
    let params = {}
    try {
        params = JSON.parse(decodeURIComponent(searchParams.get(key) ?? '{}'))
    } catch (error) {
        console.warn(`Invalid URL search params : `, searchParams.get(key))
    }
    return params;
}

export function isValidUrl(value: string): boolean {
    if (!value?.trim()) return false;

    try {
        const url = new URL(value.trim());
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

export function validateImageDimensions(blob: any, x: number, y: number): Promise<any> {
    return new Promise((resolve) => {
        if (blob) {
            const shouldRevokeUrl = blob instanceof File
            let url = shouldRevokeUrl ? URL.createObjectURL(blob) : blob
            let image = new Image()
            if (url) image.src = url;
            image.onload = () => {
                if (shouldRevokeUrl) URL.revokeObjectURL(url)
                if (image.width / x === image.height / y) return resolve(true)
                return resolve(false)
            }
            image.onerror = () => {
                if (shouldRevokeUrl) URL.revokeObjectURL(url)
                return resolve(false)
            }
        }
        else {
            resolve(true)
        }
    })
}

export function validateImageResolution(blob: any, x: number, y: number): Promise<any> {
    return new Promise((resolve) => {
        if (blob) {

            const shouldRevokeUrl = blob instanceof File
            let url = shouldRevokeUrl ? URL.createObjectURL(blob) : blob
            let image = new Image()
            if (url) image.src = url;
            image.onload = () => {
                if (shouldRevokeUrl) URL.revokeObjectURL(url)
                if (image.width === x && image.height === y) return resolve(true)
                return resolve(false)
            }
            image.onerror = () => {
                if (shouldRevokeUrl) URL.revokeObjectURL(url)
                return resolve(false)
            }
        }
        else {
            resolve(true)
        }
    })
}

export function getMediaTypeList() {
    return [
        { display: 'Audio', value: 'audio' },
        { display: 'Movie', value: 'movie' },
        { display: 'Series', value: 'series' },
        { display: 'Episode', value: 'episode' },
        { display: 'Game', value: 'game' },
        { display: 'Virtual Asset', value: 'virtualAsset' },
    ]
}

export function toTitleCase(str: string) {
    return str.replace(
        /\w\S*/g,
        function (txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        }
    );
}

export function unSlugify(str: string, intoTitleCase = false) {
    return str.split('-')?.map(arg => intoTitleCase ? toTitleCase(arg) : arg)?.join(' ')
}

export function getKey(key: any, obj: any) {
    let val = key.split('.').reduce(function (a: any, b: any) {
        return a && a[b];
    }, obj);
    return val
}

export function getTimeRelativeTime(time: any) {
    return time ? moment(time).calendar(null, {
        sameDay: '[Today] h:mm:ss A'
    }) : ''
}

export function getUpdatedSearchParams(oldValues: any, newValues: any, name: string) {
    let updated = { ...(oldValues || {}) }
    if (name === 'sort') {
        if (newValues['sort']) {
            updated['sort'] = newValues['sort']
        }
        else {
            delete updated['sort']
        }
    }
    if (name === 'pagination') {
        if (newValues['pagination']) {
            updated['pagination'] = newValues['pagination']
        }
        else {
            delete updated['pagination']
        }
    }
    if (name === 'search') {
        newValues['search'] ? updated['search'] = newValues['search']?.trim() : delete updated['search']
        delete updated['pagination']
    }
    if (name === 'fields') {
        if (newValues['fields'] && newValues['fields']?.length) {
            updated['fields'] = newValues['fields'].filter(field => field.value !== "")
        }
        else {
            delete updated['fields']
        }
        delete updated['pagination']
    }
    if (name === 'startDate') {
        if (newValues['startDate']) {
            try {
                const d = moment(newValues['startDate'])
                if (d.isValid()) {
                    updated['fromDate'] = d.format('YYYY-MM-DD')
                }
            } catch (e) {
                // ignore
            }
        } else {
            delete updated['fromDate']
        }
    }
    if (name === 'endDate') {
        if (newValues['endDate']) {
            try {
                const d = moment(newValues['endDate'])
                if (d.isValid()) {
                    updated['toDate'] = d.format('YYYY-MM-DD')
                }
            } catch (e) {
                // ignore
            }
        } else {
            delete updated['toDate']
        }
    }
    return updated
}

export function AppendJsonToFormData(json: any, formDataInstance: FormData, rootKey?: any) {
    for (let key in json) {
        if (json[key] !== null && json[key] !== undefined) {
            if (json[key] instanceof File) {
                formDataInstance.append(`${rootKey ? `${rootKey}[${key}]` : key}`, json[key])
            }
            else if (json[key] instanceof FileList) {
                let fileIndex = 0
                for (let file of json[key]) {
                    formDataInstance.append(`${rootKey ? `${rootKey}[${key}]` : key}[${fileIndex}]`, file)
                    fileIndex += 1
                }
            }
            else if (typeof json[key] === 'object') {
                formDataInstance = AppendJsonToFormData(json[key], formDataInstance, `${rootKey ? `${rootKey}[${key}]` : key}`)
            }
            else {
                formDataInstance.append(`${rootKey ? `${rootKey}[${key}]` : key}`, json[key]?.toString())
            }
        }
    }
    return formDataInstance;
}

export function getErrorResponse(error: any): NotificationBarConfig {
    let errorObj: NotificationBarConfig = {
        title: error?.response?.data?.title ?? error?.response?.data?.error ?? error?.statusText ?? "Error",
        message: error?.response?.data?.message ?? error?.response?.data?.error ?? error?.message ?? error,
        type: 'error'
    }

    if (error?.response?.status === 403) {
        errorObj['title'] = 'Permission denied !'
        errorObj['message'] = 'You are not allowed to do this operation, please contact super admin.'
    }

    return errorObj;
}

export function getKeyByValue(object, value) {
    return Object.keys(object ?? {}).find(key => object[key] === value);
}

export async function sleep(timeout: number) {
    return new Promise(resolve => setTimeout(resolve, timeout))
}

export function formatToLocalDateTime(utcDate: string | Date): string {
    if (!utcDate) return '';

    const date = new Date(utcDate);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-based
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}


// util/dateUtils.ts
export const getLocalDateRange = (fromDate?: Date | null, toDate?: Date | null) => {
    let from: string | undefined;
    let to: string | undefined;

    if (fromDate) {
        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0); // local start of day
        from = start.toISOString();
    }

    if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999); // local end of day
        to = end.toISOString();
    }

    return { from, to };
};


export function extractSvg(svg: string) {
    const viewBox =
        svg.match(/viewBox="([^"]+)"/)?.[1] || "0 0 24 24";

    const content = svg
        .replace(/^<svg[^>]*>/, "")
        .replace("</svg>", "");

    return { viewBox, content };
}

export function minifySvg(svg: string) {
    return svg
        .replace(/\n/g, "")
        .replace(/\s{2,}/g, " ")
        .replace(/<!--.*?-->/g, "")
        .trim();
}

export const getImageSrc = (thumbnail: string | undefined | null): string => {
    if (!thumbnail) return "";
    if (thumbnail.startsWith("blob:")) return thumbnail;
    if (thumbnail.startsWith("http")) return thumbnail;
    return `${bucketURL}${thumbnail}`;
};

export const stripCloudFrontPrefix = (url?: string) => {
    if (!url) return ''
    return url.replace(/^https?:\/\/[^/]+/, '')
}

export const removeCloudFrontPrefix = (thumbnail: string): string => {
    if (!thumbnail) return ''
    if (thumbnail.startsWith('blob:')) return thumbnail

    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
        const cloudfrontPattern = /^https?:\/\/[^/]+\.cloudfront\.net\/(.+)$/i
        const match = thumbnail.match(cloudfrontPattern)
        if (match && match[1]) {
            return match[1]
        }
        const bucketURL = process.env.REACT_APP_AWS_CLOUD_FRONT_URL || ''
        if (bucketURL && thumbnail.startsWith(bucketURL)) {
            return thumbnail.replace(bucketURL, '')
        }
    }
    return thumbnail
}

export const capitalize = (value?: string) =>
    value ? value[0].toUpperCase() + value.slice(1).toLowerCase() : '';


export const findFirstErrorField = (errors: any, parentKey = ''): string | null => {
    for (const key in errors) {
        const value = errors[key]
        const fieldPath = parentKey ? `${parentKey}.${key}` : key

        if (value?.message) return fieldPath

        if (typeof value === 'object') {
            const deep = findFirstErrorField(value, fieldPath)
            if (deep) return deep
        }
    }
    return null
}


export const findFirstErrorInSection = (
    errors: any,
    section: string
): string | null => {
    if (!errors?.[section]) return null

    const field = findFirstErrorField(errors[section])
    return field ? `${section}.${field}` : null
}


export const normalizeIconPath = (url: string | null | undefined): string => {
    if (!url) return '';

    // If already correct (icons/...)
    if (url.startsWith('icons/')) {
        return url;
    }

    // Remove duplicated domain if exists
    const iconIndex = url.indexOf('icons/');

    if (iconIndex !== -1) {
        return url.substring(iconIndex);
    }

    return url;
};
export const getRelativeIconPath = (icon: string | null) => {
    if (!icon) return '';

    try {
        // If it's a full URL → convert to relative path
        if (icon.startsWith('http')) {
            return new URL(icon).pathname.replace(/^\/+/, '');
        }
        return icon; // already relative
    } catch {
        return icon;
    }
};


// Parse API response
export interface ParsedDetail {
    label: string
    items: string[]
}

export const parseErrorDetails = (details: string[] = []): ParsedDetail[] => {
    return details.map((item: string) => {
        const [label, ...rest] = item.split(':')
        const values = rest.join(':') // safe join

        const items = values
            ?.split(',')
            .map(v => v.trim())
            .filter(Boolean)

        return {
            label: label?.trim(),
            items,
        }
    })
}

// Utility Case function
export const formatLabel = (value?: string) => {
    if (!value) return '—';

    return value
        .replace(/[_-]+/g, ' ') // replace _ and - with space
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize first letter
};
